'use client';

import { useMemo, useState } from "react";
import Image from "next/image";
import {
    Box,
    Button,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Typography,
} from "@mui/material";
import { Collections, Delete, PictureAsPdf } from "@mui/icons-material";
import { DatePicker, DateView, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import {
    Enum_Historyevent_Showdateas,
    HistoryEvent,
    UploadFile,
} from "@/app/lib/generated/graphql-types";
import { useSiteHistoryEditor } from "@/app/features/sites/history/contexts/SiteHistoryEditor";
import HistoryEventEditorProvider, {
    useHistoryEventEditor,
} from "@/app/features/sites/history/contexts/HistoryEventEditorContext";
import SlideshowIntroV2 from "@/app/__to_be_integrated/_____components/_NEW/intros/slideshow/SlideshowIntroV2";
import EditableTypographyV2 from "@/app/features/shared/editable-typography/EditableTypographyV2";
import EditableMarkdownV2 from "@/app/features/shared/editable-markdown/EditableMarkdownV2";
import VisiblyScrollableV2 from "@/app/features/shared/__to_be_integrated/ui/v2/VisiblyScrollable";
import EditMediaButtonOverlay from "@/app/features/shared/EditMediaButtonOverlay";
import { MediaBrowserV2Dialog } from "@/app/features/shared/media-browser-v2";
import {
    mediaRefAlt,
    mediaRefDimensions,
    mediaRefName,
    mediaRefUrl,
} from "@/app/features/shared/__to_be_integrated/types/general/media/media-helper";
import {
    HistoryEventViewFrame,
    historyEventFrameStyles as eventStyles,
} from "@/app/features/sites/history/ui/shared/HistoryEventFrames";
import {
    HistoryEventSectionFrame,
    HistoryViewIntroFrame,
    HistoryViewScrollSnapFrame,
} from "@/app/features/sites/history/ui/shared/HistoryViewFrames";
import ShowroomEditable from "@/app/features/shared/showroom/ui/editable/Showroom.editable";
import HistoryTimelinePublic from "@/app/features/sites/history/ui/shared/HistoryTimeline.public.client";
import {
    HISTORY_INTRO_SECTION_ID,
    HistoryTimelineItem,
} from "@/app/features/sites/history/ui/shared/HistoryTimeline.types";
import IntroAnchor from "@/app/features/shared/__to_be_integrated/ui/IntroAnchor";
import { toast } from "react-toastify";
import { useRouter } from "next/navigation";
import ApprovalDialog from "@/app/features/shared/__to_be_integrated/ui/dialogs/ApprovalDialog";
import { deleteHistoryEvent } from "@/app/features/sites/history/data-access/mutations";

const HISTORY_SHOWROOM_SECTION_ID = 'history-showroom';
const yearFormatter = new Intl.DateTimeFormat('de-DE', { year: 'numeric' });
const breakoutFieldSx = {
    '& .MuiOutlinedInput-root': {
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'breakout.main',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'breakout.light',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'breakout.main',
        },
    },
};
const breakoutSelectSx = {
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'breakout.main',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'breakout.light',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'breakout.main',
    },
};

function datePickerSettings(showDateAs: Enum_Historyevent_Showdateas): {
    views: DateView[];
    openTo: DateView;
    format: string;
} {
    switch (showDateAs) {
        case Enum_Historyevent_Showdateas.YearOnly:
            return { views: ['year'], openTo: 'year', format: 'yyyy' };
        case Enum_Historyevent_Showdateas.MonthAndYear:
            return { views: ['year', 'month'], openTo: 'month', format: 'MM.yyyy' };
        case Enum_Historyevent_Showdateas.DayAndYear:
        default:
            return { views: ['year', 'month', 'day'], openTo: 'day', format: 'dd.MM.yyyy' };
    }
}

function normalizeHistoryDate(date: DateTime, showDateAs: Enum_Historyevent_Showdateas): string | null {
    const normalizedDate = showDateAs === Enum_Historyevent_Showdateas.YearOnly
        ? date.startOf('year')
        : showDateAs === Enum_Historyevent_Showdateas.MonthAndYear
            ? date.startOf('month')
            : date.startOf('day');

    return normalizedDate.toISODate();
}

export default function HistoryViewEditable({
    events,
}: {
    events: HistoryEvent[];
}) {
    const { editable, updateEditable, errorForField } = useSiteHistoryEditor();
    const showroom = editable?.showroom;

    const timelineItems = useMemo<HistoryTimelineItem[]>(() => [
        {
            id: HISTORY_INTRO_SECTION_ID,
            label: '',
            ariaLabel: 'Zum Anfang der Geschichte springen',
            kind: 'intro',
        },
        ...events.map((event) => ({
            id: `history-event-${event.documentId}`,
            label: yearFormatter.format(new Date(`${event.startDate}T00:00:00`)),
            ariaLabel: `Zum Ereignis ${event.title} springen`,
            kind: 'event' as const,
        })),
        ...(showroom ? [{
            id: HISTORY_SHOWROOM_SECTION_ID,
            label: '',
            ariaLabel: 'Zum Ausstellungsbereich springen',
            kind: 'showroom' as const,
        }] : []),
    ], [events, showroom]);

    if (!editable) return null;

    const introImages = editable.intro.image ? [editable.intro.image] : [];
    const titleError = errorForField?.('intro.title') || undefined;
    const subtitleError = errorForField?.('intro.subtitle') || undefined;

    return (
        <>
            <HistoryViewScrollSnapFrame>
                <HistoryViewIntroFrame sectionId={HISTORY_INTRO_SECTION_ID}>
                    <SlideshowIntroV2
                        title={editable.intro.title}
                        subtitle={editable.intro.subtitle}
                        images={introImages}
                        linkContentToAnchor={false}
                        startChildren={(
                            <EditMediaButtonOverlay
                                layout="inline"
                                onImageChange={(image) => {
                                    if (image) updateEditable({ intro: { image } });
                                }}
                            />
                        )}
                        content={(
                            <Stack spacing={1} alignItems="center">
                                <EditableTypographyV2
                                    label="Titel"
                                    required
                                    component="h1"
                                    variant="h1"
                                    value={editable.intro.title}
                                    onChange={(title) => updateEditable({ intro: { title } })}
                                    error={Boolean(titleError)}
                                    helperText={titleError}
                                    sx={{
                                        fontSize: 'clamp(2rem, 4.8vw, 4rem)',
                                        lineHeight: 1.1,
                                        textWrap: 'balance',
                                    }}
                                />
                                <EditableTypographyV2
                                    label="Untertitel"
                                    component="h2"
                                    variant="subtitle2"
                                    value={editable.intro.subtitle ?? ''}
                                    onChange={(subtitle) => updateEditable({ intro: { subtitle } })}
                                    error={Boolean(subtitleError)}
                                    helperText={subtitleError}
                                    sx={{
                                        fontSize: 'clamp(1rem, 2.1vw, 1.5rem)',
                                        lineHeight: 1.35,
                                        textWrap: 'pretty',
                                    }}
                                />
                            </Stack>
                        )}
                    />
                </HistoryViewIntroFrame>
                <IntroAnchor />

                {events.map((event) => {
                    const headingId = `history-event-heading-${event.documentId}`;
                    return (
                        <HistoryEventSectionFrame
                            key={event.documentId}
                            sectionId={`history-event-${event.documentId}`}
                            headingId={headingId}
                        >
                            <HistoryEventEditorProvider documentId={event.documentId}>
                                <HistoryEventEditable headingId={headingId} documentId={event.documentId} />
                            </HistoryEventEditorProvider>
                        </HistoryEventSectionFrame>
                    );
                })}

                {showroom && (
                    <ShowroomEditable
                        data={showroom}
                        onTitleChange={(title) => updateEditable({ showroom: { ...showroom, title } })}
                        onSubtitleChange={(subtitle) => updateEditable({ showroom: { ...showroom, subtitle } })}
                        onDescriptionChange={(description) => updateEditable({ showroom: { ...showroom, description } })}
                        onImagesChange={(images) => updateEditable({ showroom: { ...showroom, images } })}
                        reserveRightRail
                        sectionId={HISTORY_SHOWROOM_SECTION_ID}
                    />
                )}
            </HistoryViewScrollSnapFrame>
            <HistoryTimelinePublic items={timelineItems} />
        </>
    );
}

function HistoryEventEditable({ headingId, documentId }: { headingId: string; documentId: string }) {
    const { editable, updateEditable, errorForField } = useHistoryEventEditor();
    const router = useRouter();
    const [mediaBrowserTarget, setMediaBrowserTarget] = useState<'image' | 'readMore' | null>(null);
    const [isDescriptionEditing, setIsDescriptionEditing] = useState(false);
    const [isDeleteApprovalOpen, setIsDeleteApprovalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    if (!editable) return null;

    const titleError = errorForField?.('title') || undefined;
    const descriptionError = errorForField?.('description') || undefined;
    const startDateError = errorForField?.('startDate') || undefined;
    const endDateError = errorForField?.('endDate') || undefined;
    const image = editable.image as UploadFile | null | undefined;
    const dimensions = image ? mediaRefDimensions(image) : null;
    const pickerSettings = datePickerSettings(editable.showDateAs);
    const readMoreName = editable.readMore ? mediaRefName(editable.readMore) : undefined;
    const readMoreFileName = readMoreName
        ? (readMoreName.toLowerCase().endsWith('.pdf') ? readMoreName : `${readMoreName}.pdf`)
        : 'PDF-Datei';

    const handleDelete = async () => {
        setIsDeleting(true);
        const result = await deleteHistoryEvent(documentId);
        if (result.error) {
            toast.error(`Das Ereignis konnte nicht gelöscht werden: ${result.error.message}`);
            setIsDeleting(false);
            return;
        }
        setIsDeleteApprovalOpen(false);
        router.refresh();
    };

    const imageEditor = image ? (
        <figure className={eventStyles.imageMotion}>
            <div className={eventStyles.imageScaleMotion}>
                <div className={eventStyles.imageFrame}>
                    <Image
                        src={mediaRefUrl(image, { size: 'medium' })}
                        alt={mediaRefAlt(image) ?? ''}
                        width={dimensions?.width ?? 600}
                        height={dimensions?.height ?? 800}
                        sizes="(min-width: 900px) 26vw, 92vw"
                        className={eventStyles.image}
                    />
                    <EditMediaButtonOverlay onImageChange={(nextImage) => updateEditable({ image: nextImage })} />
                </div>
            </div>
        </figure>
    ) : undefined;

    return (
        <>
            <Box sx={{ position: 'relative' }}>
                <HistoryEventViewFrame
                    image={imageEditor}
                    date={(
                    <Stack spacing={1} sx={{ width: '100%' }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="de">
                                <DatePicker
                                    label="Startdatum"
                                    value={DateTime.fromISO(editable.startDate)}
                                    views={pickerSettings.views}
                                    openTo={pickerSettings.openTo}
                                    format={pickerSettings.format}
                                    onChange={(date) => {
                                        if (date?.isValid) {
                                            updateEditable({
                                                startDate: normalizeHistoryDate(date, editable.showDateAs) ?? editable.startDate,
                                            });
                                        }
                                    }}
                                    slotProps={{
                                        textField: {
                                            required: true,
                                            error: Boolean(startDateError),
                                            helperText: startDateError,
                                            sx: breakoutFieldSx,
                                        },
                                    }}
                                />
                                <DatePicker
                                    label="Enddatum"
                                    value={editable.endDate ? DateTime.fromISO(editable.endDate) : null}
                                    views={pickerSettings.views}
                                    openTo={pickerSettings.openTo}
                                    format={pickerSettings.format}
                                    onChange={(date) => updateEditable({
                                        endDate: date?.isValid
                                            ? normalizeHistoryDate(date, editable.showDateAs)
                                            : null,
                                    })}
                                    slotProps={{
                                        textField: {
                                            error: Boolean(endDateError),
                                            helperText: endDateError,
                                            sx: breakoutFieldSx,
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                            <FormControl error={Boolean(errorForField?.('showDateAs'))}>
                                <InputLabel id={`${headingId}-date-format-label`}>Anzeige</InputLabel>
                                <Select
                                    labelId={`${headingId}-date-format-label`}
                                    label="Anzeige"
                                    value={editable.showDateAs}
                                    onChange={(event) => {
                                        const showDateAs = event.target.value as Enum_Historyevent_Showdateas;
                                        updateEditable({ showDateAs });
                                    }}
                                    sx={breakoutSelectSx}
                                >
                                    <MenuItem value={Enum_Historyevent_Showdateas.YearOnly}>Nur Jahr</MenuItem>
                                    <MenuItem value={Enum_Historyevent_Showdateas.MonthAndYear}>Monat und Jahr</MenuItem>
                                    <MenuItem value={Enum_Historyevent_Showdateas.DayAndYear}>Tag und Jahr</MenuItem>
                                </Select>
                                <FormHelperText>{errorForField?.('showDateAs')}</FormHelperText>
                            </FormControl>
                        </Stack>
                    </Stack>
                )}
                title={(
                    <EditableTypographyV2
                        id={headingId}
                        label="Titel"
                        required
                        component="h2"
                        variant="h2"
                        value={editable.title}
                        onChange={(title) => updateEditable({ title })}
                        error={Boolean(titleError)}
                        helperText={titleError}
                        sx={{
                            color: 'inherit',
                            fontSize: 'inherit',
                            fontWeight: 'inherit',
                            lineHeight: 'inherit',
                        }}
                    />
                )}
                description={(
                    <VisiblyScrollableV2
                        scrollDisabled={isDescriptionEditing}
                        scrollContainerAriaLabel={`Beschreibung zu ${editable.title}`}
                        className={eventStyles.descriptionScrollable}
                        sx={isDescriptionEditing ? {
                            overflow: 'visible',
                            '& .visibly-scrollable-inner': {
                                overflow: 'visible',
                            },
                        } : undefined}
                    >
                        <EditableMarkdownV2
                            label="Beschreibung"
                            required
                            ariaLabel={`Beschreibung zu ${editable.title} bearbeiten`}
                            value={editable.description}
                            onChange={(description) => updateEditable({ description })}
                            onEditingChange={setIsDescriptionEditing}
                            error={Boolean(descriptionError)}
                            helperText={descriptionError}
                            slots={{
                                root: {
                                    sx: {
                                        width: '100%',
                                        minHeight: 200,
                                        '& .markdown-editor': {
                                            overflow: 'visible !important',
                                        },
                                    },
                                },
                            }}
                        />
                    </VisiblyScrollableV2>
                    )}
                    actions={(
                    <Stack spacing={1} sx={{ width: '100%' }}>
                        {editable.readMore && (
                            <Box
                                role="status"
                                aria-live="polite"
                                title={readMoreFileName}
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    alignItems: 'center',
                                    width: 'fit-content',
                                    maxWidth: '100%',
                                    minHeight: 40,
                                    px: 1.5,
                                    py: 0.75,
                                    border: 1,
                                    borderColor: 'breakout.main',
                                    borderRadius: 1,
                                    backgroundColor: 'background.paper',
                                }}
                            >
                                <PictureAsPdf aria-hidden sx={{ flex: '0 0 auto', color: 'breakout.main' }} />
                                <Typography
                                    component="span"
                                    variant="body2"
                                    sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                >
                                    <Box component="span" sx={{ fontWeight: 700 }}>PDF gesetzt:</Box>{' '}
                                    {readMoreFileName}
                                </Typography>
                            </Box>
                        )}
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            {!image && (
                                <Button
                                    variant="outlined"
                                    color="breakout"
                                    startIcon={<Collections />}
                                    onClick={() => setMediaBrowserTarget('image')}
                                >
                                    Bild auswählen
                                </Button>
                            )}
                            <Button
                                variant="outlined"
                                color="breakout"
                                startIcon={<PictureAsPdf />}
                                onClick={() => setMediaBrowserTarget('readMore')}
                            >
                                {editable.readMore ? 'PDF ersetzen' : 'PDF hinzufügen'}
                            </Button>
                            {editable.readMore && (
                                <Button
                                    variant="text"
                                    color="error"
                                    startIcon={<Delete />}
                                    onClick={() => updateEditable({ readMore: null })}
                                >
                                    PDF entfernen
                                </Button>
                            )}
                        </Stack>
                        <Button
                            fullWidth
                            variant="outlined"
                            color="error"
                            startIcon={<Delete />}
                            disabled={isDeleting}
                            onClick={() => setIsDeleteApprovalOpen(true)}
                        >
                            Ereignis löschen
                        </Button>
                    </Stack>
                    )}
                />
            </Box>

            <MediaBrowserV2Dialog
                isOpen={mediaBrowserTarget !== null}
                onApply={(media) => {
                    if (mediaBrowserTarget === 'image') {
                        if (media && !media.mime?.startsWith('image/')) {
                            toast.error('Bitte eine Bilddatei auswählen.');
                            return;
                        }
                        updateEditable({ image: media });
                    } else if (mediaBrowserTarget === 'readMore') {
                        if (media && media.mime !== 'application/pdf') {
                            toast.error('Bitte eine PDF-Datei auswählen.');
                            return;
                        }
                        updateEditable({ readMore: media });
                    }
                    setMediaBrowserTarget(null);
                }}
                onClose={() => setMediaBrowserTarget(null)}
            />
            <ApprovalDialog
                open={isDeleteApprovalOpen}
                variant="warning"
                title="Ereignis löschen?"
                content={<>„{editable.title}“ wird endgültig gelöscht. Dieser Vorgang kann nicht rückgängig gemacht werden.</>}
                onDecline={() => setIsDeleteApprovalOpen(false)}
                onApprove={() => void handleDelete()}
                approveButtonLabel={isDeleting ? 'Wird gelöscht …' : 'Ja, endgültig löschen!'}
            />
        </>
    );
}
